import * as React from 'react';
import { useFetcher } from '@remix-run/react';
import { useSetAtom } from 'jotai';
import { CommentForm } from './CommentForm';
import type { CommentSubmitPayload } from '@modules/build-hub-comment/types';
import type { Candidate } from '@modules/build-hub-comment/components/CommentAutoCompleter';
import { CREATE_BUILD_HUB_COMMENT_API_ROUTE } from '~/routes/remix-api.app.build-hub-comment-create';
import { useBuildHubData } from '@modules/build-hub/hooks/use-build-hub-data';
import { useBuildHubRealTimeContext } from '@modules/build-hub/context/BuildHubRealTimeContext';
import { uiStateAtomManageCollabratorModalIsOpened } from '@modules/build-hub/components/build-hub-admin/ManageCollabratorModal';

export function CommentBox({
    documentId,
    onPosted,
    onSuccess,
}: {
    documentId?: string;
    onPosted?: (payload: CommentSubmitPayload) => void;
    onSuccess?: () => void;
}) {
    const fetcher = useFetcher();
    const { buildHub } = useBuildHubData();
    const { triggerCommentUpdate } = useBuildHubRealTimeContext();
    const setManageModalOpen = useSetAtom(
        uiStateAtomManageCollabratorModalIsOpened
    );

    const userCandidates: Candidate[] = buildHub.collaborators.map((c) => ({
        type:
            String(c.id) === String(buildHub.owner.id)
                ? ('owner' as const)
                : ('user' as const),
        id: String(c.id),
        avatar: c.image,
        label: c.name.length > 0 ? c.name : c.email,
    }));

    // Add "Invite new user" action if user has edit permissions
    const actionCandidates: Candidate[] = buildHub.userPermissions.canEdit
        ? [
              {
                  type: 'action' as const,
                  id: 'invite-user',
                  avatar: null,
                  label: 'Invite new user',
                  onAction: () => {
                      setManageModalOpen(true);
                  },
              },
          ]
        : [];

    const candidates: Candidate[] = [...userCandidates, ...actionCandidates];

    // Handle fetcher errors
    React.useEffect(() => {
        if (fetcher.state === 'idle' && fetcher.data) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const data = fetcher.data as any;
            const result = data?.commentSubmit || data?.buildHubUpsert;

            if (result) {
                const typename = result.__typename;

                // Check for GraphQL error types
                if (
                    typename === 'ServerError' ||
                    typename === 'UserError' ||
                    typename === 'NotAuthorizedError' ||
                    typename === 'NotFoundError'
                ) {
                    console.error('Failed to post comment:', result.Message);
                } else if (typename === 'BuildHubComment') {
                    // Success - comment was created
                    console.log('Comment posted successfully');

                    // Manually trigger WebSocket callback immediately (simulates WebSocket message)
                    // This avoids waiting 3-4 seconds for the actual WebSocket message
                    // The callback will trigger refetchComments in the Comments component
                    if (documentId && buildHub.id) {
                        triggerCommentUpdate({
                            type: 'comment',
                            buildhub_id: Number(buildHub.id),
                            document_id: Number(documentId),
                            comment_id: Number(result.ID),
                            action: 'create',
                        });
                    }

                    // Fallback: call onSuccess if provided (for backward compatibility)
                    onSuccess?.();
                }
            }
        }
    }, [
        fetcher.state,
        fetcher.data,
        onSuccess,
        documentId,
        buildHub.id,
        triggerCommentUpdate,
    ]);

    async function createComment(payload: CommentSubmitPayload) {
        const formData = new FormData();

        if (payload.context?.documentId) {
            formData.append('targetId', payload.context.documentId);
        }

        formData.append('message', payload.message);

        // Always include the owner ID for the current user
        formData.append('ownerId', String(buildHub.owner.id));

        for (const entity of payload.entities) {
            if (entity.type === 'user') {
                formData.append('mentionIds', entity.id);
            } else if (entity.type === 'file') {
                formData.append('fileIds', entity.id);
            }
        }

        // Use fetcher.submit for optimistic updates
        fetcher.submit(formData, {
            method: 'POST',
            action: CREATE_BUILD_HUB_COMMENT_API_ROUTE,
        });

        // Call onPosted immediately for optimistic UI
        onPosted?.(payload);
    }

    const isSubmitting = fetcher.state === 'submitting';

    return (
        <CommentForm
            documentId={documentId}
            candidates={candidates}
            onSubmit={async (payload) => {
                await createComment(payload);
            }}
            autoFocus={true}
            disabled={isSubmitting}
        />
    );
}
